import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const OVERLAY_ANIMATION = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 } };
const IMAGE_ANIMATION = { initial: { opacity: 0, scale: 0.97 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0 }, transition: { duration: 0.25 } };

export function PhotoLightbox({ photos, index, onIndexChange, onClose }) {
  const touchStartX = useRef(null);
  const photo = photos[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndexChange((index + 1) % photos.length);
      else if (e.key === "ArrowLeft") onIndexChange((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onIndexChange, onClose]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) onIndexChange((index + (delta < 0 ? 1 : -1) + photos.length) % photos.length);
    touchStartX.current = null;
  };

  if (!photo) return null;
  return (
    <motion.div data-testid="listing-lightbox" {...OVERLAY_ANIMATION}
      onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      <button data-testid="listing-lightbox-close" onClick={onClose} className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center z-10 text-white"><X size={20} /></button>
      {photos.length > 1 && (
        <button data-testid="listing-lightbox-prev" onClick={(e) => { e.stopPropagation(); onIndexChange((index - 1 + photos.length) % photos.length); }} className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center z-10 text-white"><ChevronLeft size={22} /></button>
      )}
      {photos.length > 1 && (
        <button data-testid="listing-lightbox-next" onClick={(e) => { e.stopPropagation(); onIndexChange((index + 1) % photos.length); }} className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center z-10 text-white"><ChevronRight size={22} /></button>
      )}
      <AnimatePresence mode="wait">
        <motion.img key={photo.id} src={photo.url} alt="" onClick={(e) => e.stopPropagation()}
          {...IMAGE_ANIMATION}
          className="max-h-[88vh] max-w-[92vw] object-contain select-none" data-testid="listing-lightbox-image" />
      </AnimatePresence>
      {photos.length > 1 && <div className="absolute bottom-5 inset-x-0 text-center text-white/50 text-xs font-mono">{index + 1} / {photos.length}</div>}
    </motion.div>
  );
}
